import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, LogOut, Users, IndianRupee, ListChecks, Mail, CheckCircle2, XCircle, Map, QrCode, Camera, Download, UserPlus2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import Logo from "../components/Logo";
import AdminLiveMap from "../components/AdminLiveMap";
import { AdminCommunicate, AdminReminders, AdminTasks, AdminQrOnboard, AdminSegmentedUsers } from "../components/AdminExtras";
import { AdminFlatmate } from "../components/AdminFlatmate";
import { AdminHomeStay } from "../components/homestay/AdminHomeStay";
import { AdminAccounts } from "../components/AdminAccounts";
import { AdminNameCraft } from "./NameCraft";
import { photoUrl } from "../lib/photos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "heylpmate_admin_token";

function useAdminAuth() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const login = async (email, password) => {
    const r = await axios.post(`${API}/admin/login`, { email, password });
    localStorage.setItem(TOKEN_KEY, r.data.token);
    setToken(r.data.token);
  };
  const logout = () => { localStorage.removeItem(TOKEN_KEY); setToken(null); };
  const fetch = (path) => axios.get(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  const post = (path) => axios.post(`${API}${path}`, {}, { headers: { Authorization: `Bearer ${token}` } });
  return { token, login, logout, fetch, post };
}

function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onLogin(email, password); toast.success("Welcome, admin"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); setBusy(false); }
  };
  return (
    <div className="min-h-[80vh] grid place-items-center px-5">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-block mb-6"><Logo tagline /></Link>
        <div className="chip mb-4" style={{ borderColor: "var(--primary)", color: "var(--primary)" }}>
          <ShieldCheck className="w-3.5 h-3.5" /> Admin only
        </div>
        <h1 className="font-display text-4xl font-bold">Admin sign in</h1>
        <p className="text-sm text-neutral-500 mt-2">Separate login for HeylpMate platform admins.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-500">Email</Label>
            <Input data-testid="admin-email" type="email" className="mt-1.5" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-neutral-500">Password</Label>
            <Input data-testid="admin-password" type="password" className="mt-1.5" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          <button data-testid="admin-login-btn" className="btn-navy w-full" disabled={busy}>
            {busy ? "Signing in…" : "Sign in as Admin"}
          </button>
        </form>
      </div>
    </div>
  );
}

function StatCard({ i: Icon, label, value, hint }) {
  return (
    <div className="rounded-3xl border border-black/10 bg-white p-5 card-lift">
      <div className="w-10 h-10 rounded-full grid place-items-center text-white" style={{ background: "var(--primary)" }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="text-xs uppercase tracking-widest text-neutral-500 mt-4">{label}</div>
      <div className="font-display text-3xl mt-1" style={{ color: "var(--primary)" }}>{value}</div>
      {hint && <div className="text-xs text-neutral-500 mt-1">{hint}</div>}
    </div>
  );
}

function RevenueMeter({ token }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/revenue/summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data)).catch(() => {});
  }, [token]);
  if (!data) return null;
  const pct = Math.min(100, data.pct_of_target || 0);
  return (
    <div className="mt-8 rounded-3xl p-6 md:p-7 text-white shadow-xl relative overflow-hidden"
         style={{ background: "linear-gradient(135deg,#00604A 0%,#0B7A5A 55%,#F45118 130%)" }}
         data-testid="admin-revenue-meter">
      <div className="absolute inset-0 opacity-20"
           style={{ backgroundImage: "radial-gradient(circle at 20% 30%,#fff 0,transparent 40%),radial-gradient(circle at 80% 70%,#fff 0,transparent 40%)" }} />
      <div className="relative flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest opacity-90">₹100 Cr Revenue Target</div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="text-xl opacity-90">₹</span>
            <span className="font-display text-5xl md:text-6xl font-bold">
              {(data.total_inr || 0).toLocaleString("en-IN")}
            </span>
          </div>
          <div className="text-xs mt-1 opacity-90">
            of ₹{(data.target_inr || 0).toLocaleString("en-IN")} · <b>{pct}%</b> reached
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-widest opacity-90">Today</div>
          <div className="mt-2 font-display text-3xl font-bold">₹{(data.today_inr || 0).toLocaleString("en-IN")}</div>
          <div className="text-[10px] mt-1 opacity-80">UPI · ₹{data.breakdown?.universal_upi || 0} · Stripe · ₹{data.breakdown?.stripe || 0}</div>
        </div>
      </div>
      {/* Progress bar */}
      <div className="relative mt-5 h-3 rounded-full bg-white/20 overflow-hidden">
        <div className="absolute left-0 top-0 bottom-0 bg-white shadow-lg"
             style={{ width: `${Math.max(1, pct)}%`, transition: "width 900ms" }} />
      </div>
    </div>
  );
}

function PlatformControl({ token }) {
  const [settings, setSettings] = useState(null);
  const [prices, setPrices] = useState({});
  const [modules, setModules] = useState({});
  const [target, setTarget] = useState(100);
  const [busy, setBusy] = useState(false);
  const [wa, setWa] = useState({ to_phone: "", to_user_id: "", message: "" });

  useEffect(() => {
    axios.get(`${API}/admin/platform/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const s = r.data.settings || {};
        setSettings(s);
        setPrices(s.prices || {});
        setModules(s.modules || {});
        setTarget(s.revenue_target_cr || 100);
      }).catch(() => {});
  }, [token]);

  const save = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/platform/settings`,
        { prices, modules, revenue_target_cr: Number(target) || 100 },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Platform settings saved — live everywhere");
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };
  const sendWa = async () => {
    if (!wa.message || (!wa.to_phone && !wa.to_user_id)) { toast.error("Add phone/user + message"); return; }
    try {
      const r = await axios.post(`${API}/admin/whatsapp/send`, wa,
        { headers: { Authorization: `Bearer ${token}` } });
      window.open(r.data.wa_url, "_blank");
      toast.success("WhatsApp opened + logged");
      setWa({ to_phone: "", to_user_id: "", message: "" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };
  const downloadCsv = async (name, path) => {
    const url = `${API}${path}`;
    try {
      const r = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
      const blob = new Blob([r.data]);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${name}.csv`; a.click();
    } catch (e) { toast.error("Export failed"); }
  };

  if (!settings) return <div className="rounded-2xl border p-8 text-center text-sm text-neutral-500">Loading platform settings…</div>;

  return (
    <div className="space-y-5" data-testid="platform-control">
      {/* Revenue target */}
      <div className="rounded-2xl border border-black/10 bg-white p-5">
        <div className="text-xs uppercase tracking-widest text-neutral-500">Revenue Target</div>
        <div className="mt-2 flex items-center gap-3">
          <input data-testid="revenue-target-input" type="number" value={target} onChange={(e) => setTarget(e.target.value)}
                 className="w-32 border-2 rounded-xl px-3 py-1.5 text-sm" style={{ borderColor: "#E4E7E5" }} />
          <span className="text-sm font-bold" style={{ color: "#00604A" }}>Crore ₹</span>
        </div>
      </div>

      {/* Prices */}
      <div className="rounded-2xl border border-black/10 bg-white p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl" style={{ color: "#00604A" }}>Master Pricing Table</h3>
          <button data-testid="platform-save" onClick={save} disabled={busy}
                  className="btn-primary text-sm px-4 py-2">{busy ? "Saving…" : "💾 Save all"}</button>
        </div>
        <p className="text-xs mt-1" style={{ color: "#667085" }}>Every value goes live immediately across all screens & flows.</p>
        <div className="mt-4 grid md:grid-cols-2 gap-3">
          {Object.entries(prices).map(([k, v]) => (
            <label key={k} className="block">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500">{k.replace(/_/g, " ")}</span>
              <input data-testid={`price-${k}`}
                     type="number" value={v}
                     onChange={(e) => setPrices({ ...prices, [k]: Number(e.target.value) })}
                     className="mt-0.5 w-full border rounded-lg px-2 py-1.5 text-sm" />
            </label>
          ))}
        </div>
      </div>

      {/* Module toggles */}
      <div className="rounded-2xl border border-black/10 bg-white p-5">
        <h3 className="font-display text-xl" style={{ color: "#00604A" }}>Module Master Switches</h3>
        <p className="text-xs mt-1" style={{ color: "#667085" }}>Toggle any module OFF — disappears from navigation & all screens instantly.</p>
        <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {Object.entries(modules).map(([k, v]) => (
            <label key={k} className="flex items-center justify-between rounded-xl border px-3 py-2.5"
                   style={{ borderColor: v ? "#00604A55" : "#E4E7E5", background: v ? "#F0FDF4" : "#fff" }}>
              <span className="text-sm font-bold capitalize" style={{ color: v ? "#00604A" : "#667085" }}>
                {k.replace(/_/g, " ")}
              </span>
              <input data-testid={`toggle-${k}`} type="checkbox" checked={!!v}
                     onChange={(e) => setModules({ ...modules, [k]: e.target.checked })}
                     className="scale-125 accent-[#00604A]" />
            </label>
          ))}
        </div>
      </div>

      {/* WhatsApp direct send */}
      <div className="rounded-2xl border border-black/10 bg-white p-5">
        <h3 className="font-display text-xl" style={{ color: "#00604A" }}>WhatsApp direct send</h3>
        <p className="text-xs mt-1" style={{ color: "#667085" }}>Sends to the target's WhatsApp + creates an in-app notification on their dashboard. All sends are logged.</p>
        <div className="mt-3 grid md:grid-cols-3 gap-2">
          <input data-testid="wa-phone" placeholder="+91 phone" value={wa.to_phone}
                 onChange={(e) => setWa({ ...wa, to_phone: e.target.value })}
                 className="border rounded-lg px-2 py-1.5 text-sm" />
          <input data-testid="wa-user-id" placeholder="or User ID" value={wa.to_user_id}
                 onChange={(e) => setWa({ ...wa, to_user_id: e.target.value })}
                 className="border rounded-lg px-2 py-1.5 text-sm" />
          <button data-testid="wa-send" onClick={sendWa} className="btn-primary text-sm px-3 py-1.5">💬 Open WhatsApp</button>
        </div>
        <textarea data-testid="wa-message" rows={3} value={wa.message} placeholder="Type message… (or paste a template)"
                  onChange={(e) => setWa({ ...wa, message: e.target.value })}
                  className="mt-2 w-full border rounded-lg px-2 py-1.5 text-sm" />
      </div>

      {/* CSV Exports */}
      <div className="rounded-2xl border border-black/10 bg-white p-5">
        <h3 className="font-display text-xl" style={{ color: "#00604A" }}>Data exports</h3>
        <p className="text-xs mt-1" style={{ color: "#667085" }}>One-tap CSV export. Filter later inside Excel.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button data-testid="csv-users" onClick={() => downloadCsv("users", "/admin/users/export.csv")}
                  className="chip">⬇ Users CSV</button>
          <button data-testid="csv-listings" onClick={() => downloadCsv("listings", "/admin/flatmate/listings.csv")}
                  className="chip">⬇ Listings CSV</button>
          <button data-testid="csv-wa" onClick={() => downloadCsv("wa_logs", "/admin/whatsapp/logs.csv")}
                  className="chip">⬇ WhatsApp logs CSV</button>
        </div>
      </div>
    </div>
  );
}


export default function Admin() {
  const nav = useNavigate();
  const { token, login, logout, fetch, post } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [mates, setMates] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [leads, setLeads] = useState([]);
  const [liveMap, setLiveMap] = useState({ bookings: [], mates: [] });
  const [upiPayments, setUpiPayments] = useState([]);
  const [upiTxns, setUpiTxns] = useState([]);
  const [photoQueue, setPhotoQueue] = useState([]);

  const refresh = async () => {
    try {
      const [s, u, b, m, w, lm, up, cp, ld, ut] = await Promise.all([
        fetch("/admin/stats"), fetch("/admin/users"), fetch("/admin/bookings"),
        fetch("/admin/mates"), fetch("/admin/waitlist"),
        fetch("/admin/live-map"), fetch("/admin/payments/upi"), fetch("/admin/community/photos"),
        fetch("/admin/leads"), fetch("/admin/upi/txns"),
      ]);
      setStats(s.data);
      setUsers(u.data.users);
      setBookings(b.data.bookings);
      setMates(m.data.mates);
      setWaitlist(w.data.waitlist);
      setLiveMap(lm.data);
      setUpiPayments(up.data.payments || []);
      setPhotoQueue(cp.data.photos || []);
      setLeads(ld.data.leads || []);
      setUpiTxns(ut.data.items || []);
    } catch (e) {
      if (e?.response?.status === 401) logout();
    }
  };

  useEffect(() => { if (token) refresh(); /* eslint-disable-next-line */ }, [token]);

  if (!token) return <AdminLogin onLogin={login} />;

  const approve = async (uid) => { await post(`/admin/mates/${uid}/approve`); toast.success("Approved"); refresh(); };
  const reject = async (uid) => { await post(`/admin/mates/${uid}/reject`); toast.success("Rejected"); refresh(); };
  const upiApprove = async (id) => { await post(`/admin/payments/upi/${id}/approve`); toast.success("Payment verified"); refresh(); };
  const upiReject = async (id) => { await post(`/admin/payments/upi/${id}/reject`); toast.success("Rejected"); refresh(); };
  const upiTxnConfirm = async (id) => { await post(`/admin/upi/txns/${id}/confirm`); toast.success("UPI claim confirmed · credits/booking updated"); refresh(); };
  const upiTxnReject = async (id) => { await post(`/admin/upi/txns/${id}/reject`); toast.success("UPI claim rejected"); refresh(); };
  const photoApprove = async (id) => { await post(`/admin/community/photos/${id}/approve`); toast.success("Approved · ₹25 credited"); refresh(); };
  const photoReject = async (id) => { await post(`/admin/community/photos/${id}/reject`); toast.success("Rejected"); refresh(); };
  const exportLeads = () => {
    axios.get(`${API}/admin/leads/export.csv`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" })
      .then((r) => {
        const blob = new Blob([r.data], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "heylpmate_homepage_leads.csv";
        link.click();
      })
      .catch(() => toast.error("Export failed"));
  };
  const exportWaitlist = () => {
    const url = `${API}/admin/waitlist.csv`;
    fetch("/admin/waitlist").then(() => {
      // Use plain window redirect with token via download link
      window.open(`${url}?t=${Date.now()}`, "_blank");
    });
    // Use fetch with auth header to trigger CSV download
    axios.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" })
      .then((r) => {
        const blob = new Blob([r.data], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "heylpmate_waitlist.csv";
        link.click();
      })
      .catch(() => toast.error("Export failed"));
  };

  const upiPending = upiPayments.filter((p) => p.payment_status === "awaiting_verification");
  const photoPending = photoQueue.filter((p) => p.status === "pending");

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 pt-10 pb-24">
      <div className="flex justify-between items-start">
        <div>
          <div className="chip mb-3" style={{ borderColor: "var(--primary)", color: "var(--primary)" }}>
            <ShieldCheck className="w-3.5 h-3.5" /> Admin console
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold">HeylpMate control room.</h1>
        </div>
        <button data-testid="admin-logout" onClick={() => { logout(); nav("/"); }} className="chip">
          <LogOut className="w-3.5 h-3.5" /> Sign out
        </button>
      </div>

      {stats && (
        <>
          <RevenueMeter token={token} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6" data-testid="admin-stats">
            <StatCard i={Users} label="Users" value={stats.users.total} hint={`${stats.users.customers} customers · ${stats.users.mates} mates`} />
            <StatCard i={ListChecks} label="Bookings" value={stats.bookings.total} hint={`${stats.bookings.paid} paid+`} />
            <StatCard i={IndianRupee} label="Platform revenue" value={`₹${(stats.revenue.platform_inr || 0).toLocaleString("en-IN")}`} hint={`GMV ₹${(stats.revenue.gmv_inr || 0).toLocaleString("en-IN")}`} />
            <StatCard i={Mail} label="Waitlist" value={stats.waitlist} hint={`${stats.mate_profiles.under_review} mates pending`} />
          </div>
        </>
      )}

      <Tabs defaultValue="platform" className="mt-10">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger data-testid="tab-platform" value="platform" style={{ color: "#F45118", fontWeight: 700 }}>⚡ Platform Control</TabsTrigger>
          <TabsTrigger data-testid="tab-livemap" value="livemap"><Map className="w-3.5 h-3.5 mr-1" /> Live Map</TabsTrigger>
          <TabsTrigger data-testid="tab-upi" value="upi"><QrCode className="w-3.5 h-3.5 mr-1" /> UPI ({upiPending.length + (upiTxns.filter(t => t.status === "awaiting_admin_confirm").length)})</TabsTrigger>
          <TabsTrigger data-testid="tab-photos" value="photos"><Camera className="w-3.5 h-3.5 mr-1" /> Photos ({photoPending.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-mates" value="mates">Mates ({mates.filter(m => m.status === "under_review").length})</TabsTrigger>
          <TabsTrigger data-testid="tab-users" value="users">Users ({users.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-bookings" value="bookings">Bookings ({bookings.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-waitlist" value="waitlist">Waitlist ({waitlist.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-communicate" value="communicate">✉️ Communicate</TabsTrigger>
          <TabsTrigger data-testid="tab-reminders" value="reminders">🔔 Reminders</TabsTrigger>
          <TabsTrigger data-testid="tab-tasks" value="tasks">📋 Tasks</TabsTrigger>
          <TabsTrigger data-testid="tab-qr" value="qr">📱 QR Onboard</TabsTrigger>
          <TabsTrigger data-testid="tab-namecraft" value="namecraft">🪶 NameCraft</TabsTrigger>
          <TabsTrigger data-testid="tab-flatmate" value="flatmate">🏠 Flatmate</TabsTrigger>
          <TabsTrigger data-testid="tab-homestay" value="homestay">🏨 HomeStay</TabsTrigger>
          <TabsTrigger data-testid="tab-accounts" value="accounts">🔑 Accounts</TabsTrigger>
          <TabsTrigger data-testid="tab-leads" value="leads"><UserPlus2 className="w-3.5 h-3.5 mr-1" /> Homepage Leads ({leads.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="platform" className="mt-6">
          <PlatformControl token={token} />
        </TabsContent>

        <TabsContent value="communicate" className="mt-6">
          <AdminCommunicate token={token} />
        </TabsContent>
        <TabsContent value="reminders" className="mt-6">
          <AdminReminders token={token} />
        </TabsContent>
        <TabsContent value="tasks" className="mt-6">
          <AdminTasks token={token} mates={mates} />
        </TabsContent>
        <TabsContent value="qr" className="mt-6">
          <AdminQrOnboard />
        </TabsContent>
        <TabsContent value="namecraft" className="mt-6">
          <AdminNameCraft token={token} />
        </TabsContent>
        <TabsContent value="flatmate" className="mt-6">
          <AdminFlatmate token={token} />
        </TabsContent>
        <TabsContent value="homestay" className="mt-6">
          <AdminHomeStay token={token} />
        </TabsContent>
        <TabsContent value="accounts" className="mt-6">
          <AdminAccounts token={token} />
        </TabsContent>

        <TabsContent value="livemap" className="mt-6">
          <div className="rounded-3xl border border-black/10 bg-white p-4 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-display text-2xl" style={{ color: "var(--primary)" }}>Active bookings map</div>
                <div className="text-xs text-neutral-500">{liveMap.bookings.length} active · {liveMap.mates.length} mates on map</div>
              </div>
            </div>
            <AdminLiveMap bookings={liveMap.bookings} mates={liveMap.mates} />
          </div>
        </TabsContent>

        <TabsContent value="upi" className="mt-6 space-y-6">
          {/* NEW — Universal UPI Claims (heylpmate@upi / PhonePe QR) */}
          <div className="rounded-2xl border border-black/10 bg-white overflow-x-auto" data-testid="upi-txns-table">
            <div className="px-4 py-3 flex items-center justify-between border-b border-black/5">
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500">Universal UPI Claims</div>
                <div className="text-sm font-bold" style={{ color: "#00604A" }}>heylpmate@upi · PhonePe QR</div>
              </div>
              <span className="chip text-[10px]">{upiTxns.filter(t => t.status === "awaiting_admin_confirm").length} pending</span>
            </div>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-neutral-500 bg-neutral-50">
                <tr><th className="p-3 text-left">User</th><th className="p-3 text-left">Purpose</th><th className="p-3">Amount</th><th className="p-3 text-left">Note</th><th className="p-3">Status</th><th className="p-3">Action</th></tr>
              </thead>
              <tbody>
                {upiTxns.map((t) => (
                  <tr key={t.id} className="border-t border-black/5">
                    <td className="p-3">
                      <div>{t.user_name || t.user_email}</div>
                      <div className="text-[10px] text-neutral-400">{t.user_phone || t.user_email}</div>
                    </td>
                    <td className="p-3">
                      <div className="font-mono text-xs">{t.purpose}</div>
                      {t.reference_id && <div className="text-[10px] text-neutral-400">ref {t.reference_id}</div>}
                    </td>
                    <td className="p-3 text-center font-bold" style={{ color: "#00604A" }}>₹{t.amount_inr}</td>
                    <td className="p-3 text-xs text-neutral-500 max-w-[200px] truncate">{t.payer_note || "—"}</td>
                    <td className="p-3 text-center"><span className="chip text-[10px]">{t.status}</span></td>
                    <td className="p-3">
                      {t.status === "awaiting_admin_confirm" && (
                        <div className="flex gap-2 justify-center">
                          <button data-testid={`upi-txn-confirm-${t.id}`} onClick={() => upiTxnConfirm(t.id)} className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Confirm</button>
                          <button data-testid={`upi-txn-reject-${t.id}`} onClick={() => upiTxnReject(t.id)} className="px-3 py-1.5 rounded-full border border-red-200 text-red-600 text-xs font-bold hover:bg-red-50 inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> Reject</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {upiTxns.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-neutral-500 text-sm">No UPI claims yet.</td></tr>}
              </tbody>
            </table>
          </div>

          {/* Existing per-booking UPI verification */}
          <div className="rounded-2xl border border-black/10 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-neutral-500 bg-neutral-50">
                <tr><th className="p-3 text-left">Booking</th><th className="p-3 text-left">Customer</th><th className="p-3">Amount</th><th className="p-3 text-left">UTR</th><th className="p-3">Status</th><th className="p-3">Action</th></tr>
              </thead>
              <tbody>
                {upiPayments.map((p) => (
                  <tr key={p.id} className="border-t border-black/5">
                    <td className="p-3">
                      <div>{p.service_name}</div>
                      <div className="text-[10px] text-neutral-400 font-mono">{p.id.slice(0, 10)}…</div>
                    </td>
                    <td className="p-3 text-neutral-500">{p.customer_name}</td>
                    <td className="p-3 text-center">₹{p.price_inr}</td>
                    <td className="p-3 font-mono text-xs">{p.upi_utr || "—"}</td>
                    <td className="p-3 text-center"><span className="chip text-[10px]">{p.payment_status}</span></td>
                    <td className="p-3">
                      {p.payment_status === "awaiting_verification" && (
                        <div className="flex gap-2 justify-center">
                          <button data-testid={`upi-approve-${p.id}`} onClick={() => upiApprove(p.id)} className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Verify</button>
                          <button data-testid={`upi-reject-${p.id}`} onClick={() => upiReject(p.id)} className="px-3 py-1.5 rounded-full border border-red-200 text-red-600 text-xs font-bold hover:bg-red-50 inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> Reject</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {upiPayments.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-neutral-500 text-sm">No UPI payments yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="photos" className="mt-6">
          <div className="grid md:grid-cols-3 gap-4">
            {photoQueue.map((p) => (
              <div key={p.id} className="rounded-2xl border border-black/10 bg-white overflow-hidden card-lift">
                <img src={photoUrl(p.storage_path)} alt="" className="w-full h-48 object-cover" />
                <div className="p-4">
                  <div className="text-xs text-neutral-500">{p.user_name} · {p.service_slug || "—"}</div>
                  <div className="text-sm mt-1 line-clamp-3">{p.caption}</div>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="chip text-[10px]">{p.status}</span>
                    {p.status === "pending" && (
                      <div className="flex gap-2">
                        <button data-testid={`photo-approve-${p.id}`} onClick={() => photoApprove(p.id)} className="btn-primary text-xs px-3 py-1.5 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> +₹25</button>
                        <button data-testid={`photo-reject-${p.id}`} onClick={() => photoReject(p.id)} className="px-3 py-1.5 rounded-full border border-red-200 text-red-600 text-xs font-bold hover:bg-red-50 inline-flex items-center gap-1"><XCircle className="w-3 h-3" /></button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {photoQueue.length === 0 && <div className="col-span-3 text-neutral-500 text-sm text-center py-8">No submissions yet.</div>}
          </div>
        </TabsContent>

        <TabsContent value="mates" className="mt-6">
          <div className="grid md:grid-cols-2 gap-3">
            {mates.map((m) => (
              <div key={m.user_id} className="rounded-2xl border border-black/10 bg-white p-5">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-display text-lg" style={{ color: "var(--primary)" }}>{m.user_id.slice(0, 8)}…</div>
                    <div className="text-xs text-neutral-500">{m.city_slug} · {m.experience_years} yrs · {m.id_type}</div>
                  </div>
                  <span className={`chip ${m.status === "approved" ? "text-emerald-700 border-emerald-200 bg-emerald-50" : m.status === "rejected" ? "text-red-700 border-red-200 bg-red-50" : "text-amber-700 border-amber-200 bg-amber-50"}`}>{m.status}</span>
                </div>
                <div className="text-xs mt-2 text-neutral-600 line-clamp-2">{m.bio}</div>
                <div className="text-[10px] text-neutral-500 mt-2">Assists: {m.services?.join(", ")}</div>
                {m.status === "under_review" && (
                  <div className="mt-3 flex gap-2">
                    <button data-testid={`approve-${m.user_id}`} onClick={() => approve(m.user_id)} className="btn-primary text-xs px-4 py-1.5 inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                    </button>
                    <button data-testid={`reject-${m.user_id}`} onClick={() => reject(m.user_id)} className="px-4 py-1.5 rounded-full border border-red-200 text-red-600 text-xs font-bold hover:bg-red-50 inline-flex items-center gap-1">
                      <XCircle className="w-3.5 h-3.5" /> Reject
                    </button>
                  </div>
                )}
              </div>
            ))}
            {mates.length === 0 && <div className="col-span-2 text-neutral-500 text-sm">No mate profiles yet.</div>}
          </div>
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <AdminSegmentedUsers token={token} />
        </TabsContent>

        <TabsContent value="bookings" className="mt-6">
          <div className="rounded-2xl border border-black/10 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-neutral-500 bg-neutral-50">
                <tr><th className="p-3 text-left">Service</th><th className="p-3 text-left">Customer</th><th className="p-3">Price</th><th className="p-3">Fee</th><th className="p-3">Status</th><th className="p-3 text-left">When</th></tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.id} className="border-t border-black/5">
                    <td className="p-3">{b.service_name}</td>
                    <td className="p-3 text-neutral-500">{b.customer_name}</td>
                    <td className="p-3 text-center">₹{b.price_inr}</td>
                    <td className="p-3 text-center">₹{b.platform_fee_inr}</td>
                    <td className="p-3 text-center"><span className="chip text-[10px]">{b.status}</span></td>
                    <td className="p-3 text-neutral-500">{new Date(b.scheduled_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="waitlist" className="mt-6">
          <div className="flex justify-end mb-3">
            <button data-testid="waitlist-export-btn" onClick={exportWaitlist} className="btn-primary text-xs px-4 py-1.5 inline-flex items-center gap-1">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
          <div className="rounded-2xl border border-black/10 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-neutral-500 bg-neutral-50">
                <tr><th className="p-3 text-left">City</th><th className="p-3 text-left">Name</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Phone</th><th className="p-3 text-left">Since</th></tr>
              </thead>
              <tbody>
                {waitlist.map((w) => (
                  <tr key={w.id} className="border-t border-black/5">
                    <td className="p-3">{w.city_name || w.city_slug}</td>
                    <td className="p-3">{w.name || "—"}</td>
                    <td className="p-3 text-neutral-500">{w.email || "—"}</td>
                    <td className="p-3">{w.phone || "—"}</td>
                    <td className="p-3 text-neutral-500 text-xs">{w.created_at?.slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="leads" className="mt-6">
          <div className="flex justify-between items-center mb-3">
            <div>
              <div className="font-display text-xl" style={{ color: "var(--primary)" }}>Homepage Leads</div>
              <div className="text-xs text-neutral-500">Captured from the homepage splash “Stay in Touch” form.</div>
            </div>
            <button data-testid="leads-export-btn" onClick={exportLeads} className="btn-primary text-xs px-4 py-1.5 inline-flex items-center gap-1">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
          </div>
          <div className="rounded-2xl border border-black/10 bg-white overflow-x-auto" data-testid="leads-table">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-widest text-neutral-500 bg-neutral-50">
                <tr>
                  <th className="p-3 text-left">Name</th>
                  <th className="p-3 text-left">Mobile</th>
                  <th className="p-3 text-left">Email</th>
                  <th className="p-3 text-left">Submitted</th>
                  <th className="p-3 text-left">Source</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-black/5">
                    <td className="p-3">{l.name}</td>
                    <td className="p-3 font-mono text-xs">{l.mobile}</td>
                    <td className="p-3 text-neutral-500">{l.email}</td>
                    <td className="p-3 text-neutral-500 text-xs">{l.created_at ? new Date(l.created_at).toLocaleString() : "—"}</td>
                    <td className="p-3"><span className="chip text-[10px]">{l.source || "Homepage Splash"}</span></td>
                  </tr>
                ))}
                {leads.length === 0 && (
                  <tr><td colSpan="5" className="p-6 text-center text-neutral-500 text-sm">No homepage leads yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
